// src/components/admin/goal/steps/Step4.js

import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Stack,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import StepNavigation from "./StepNavigation";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("pt-br");

// helper para arredondar em 2 casas decimais
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Função para gerar os dias de um período
const generateDays = (startDate, endDate) => {
  const days = [];
  let current = dayjs(startDate);
  const end = dayjs(endDate);

  while (current.isSameOrBefore(end)) {
    days.push({
      date: current.format("YYYY-MM-DD"),
      weight: 0,
      amount: 0,
    });
    current = current.add(1, "day");
  }
  return days;
};

const getActiveSellersCount = (date, sellers, absences) => {
  const currentDate = dayjs(date).format("YYYY-MM-DD");

  const normalizedAbsences = absences.map((a) => ({
    ...a,
    date: dayjs(a.date).format("YYYY-MM-DD"),
  }));

  const activeSellers = sellers.filter((seller) => {
    const startOk = dayjs(currentDate).isSameOrAfter(dayjs(seller.start_date));
    const endOk =
      !seller.end_date || dayjs(currentDate).isSameOrBefore(dayjs(seller.end_date));
    const notAbsent = !normalizedAbsences.some(
      (a) => a.user_id === seller.id && a.date === currentDate
    );
    return startOk && endOk && notAbsent;
  });

  return activeSellers.length;
};

const distributeGoal = (periods, sellers, absences, goal) => {
  const normalizedAbsences = absences.map((a) => ({
    ...a,
    date: dayjs(a.date).format("YYYY-MM-DD"),
  }));

  // soma de todos os vendedores ativos em todos os dias
  const totalSellerDays = periods.reduce(
    (sum, p) =>
      sum +
      p.days.reduce(
        (s, d) => s + getActiveSellersCount(d.date, sellers, normalizedAbsences),
        0
      ),
    0
  );

  const valuePerSeller = totalSellerDays > 0 ? goal / totalSellerDays : 0;

  let distributedTotal = 0;

  periods.forEach((period, periodIndex) => {
    period.days.forEach((day, dayIndex) => {
      const currentDate = dayjs(day.date).format("YYYY-MM-DD");

      const activeSellers = sellers.filter((seller) => {
        const startOk = dayjs(currentDate).isSameOrAfter(dayjs(seller.start_date));
        const endOk =
          !seller.end_date || dayjs(currentDate).isSameOrBefore(dayjs(seller.end_date));
        const notAbsent = !normalizedAbsences.some(
          (a) => a.user_id === seller.id && a.date === currentDate
        );
        return startOk && endOk && notAbsent;
      });

      const sellerCount = activeSellers.length;
      day.activeSellers = sellerCount;

      let dailyAmount = sellerCount * valuePerSeller;
      dailyAmount = round2(dailyAmount);
      distributedTotal += dailyAmount;

      const isLastDay =
        periodIndex === periods.length - 1 &&
        dayIndex === period.days.length - 1;

      if (isLastDay) {
        const diff = round2(goal - distributedTotal);
        dailyAmount += diff;
      }

      day.amount = dailyAmount;
    });

    // pesos proporcionais aos vendedores ativos do período
    const perDayCounts = period.days.map((d) => d.activeSellers);
    const total = perDayCounts.reduce((s, c) => s + c, 0);

    let weightSum = 0;
    period.days.forEach((d, idx) => {
      let w = total > 0 ? round2((perDayCounts[idx] / total) * 100) : 0;
      weightSum = round2(weightSum + w);

      if (idx === period.days.length - 1) {
        const diff = round2(100 - weightSum);
        w = round2(w + diff);
      }
      d.weight = w;
    });
  });

  return periods;
};

export default function PeriodTable({ goal, setGoal, sellers, absences, onBack, onNext, activeStep, onSave }) {
  const [periods, setPeriods] = useState([]);

  const getPeriodTotal = (period) =>
    period.days.reduce((sum, d) => sum + (d.amount || 0), 0);

  const totalGeral = periods.reduce((sum, p) => sum + getPeriodTotal(p), 0);

  useEffect(() => {
    if (goal?.periods?.length > 0 && periods.length === 0) {
      let peri = goal.periods.map((p) => ({
        ...p,
        days: generateDays(p.start_date, p.end_date),
      }));
      const distributed = distributeGoal(peri, sellers, absences, goal.target);
      setPeriods(distributed);
      setGoal({
        ...goal,
        periods: distributed,
      });
    }
    // eslint-disable-next-line
  }, [goal]);

  // Atualiza valores
  const handleChange = (periodIndex, dayIndex, field, value) => {
    if (field === "amount") {
      return setPeriods((prev) => {
        const updated = prev.map((p) => ({
          ...p,
          days: p.days.map((d) => ({ ...d })),
        }));

        // fixa o valor do dia editado
        updated[periodIndex].days[dayIndex].amount = round2(value);

        // mapeia todos os dias
        const flat = [];
        updated.forEach((p, pIdx) => {
          p.days.forEach((d, dIdx) => {
            flat.push({ pIdx, dIdx, date: d.date });
          });
        });

        const globalIndex =
          updated.slice(0, periodIndex).reduce((s, p) => s + p.days.length, 0) +
          dayIndex;

        const sumUntilNow = flat
          .slice(0, globalIndex + 1)
          .reduce(
            (s, ref) =>
              s + (updated[ref.pIdx].days[ref.dIdx].amount || 0),
            0
          );

        let remaining = round2(goal.target - sumUntilNow);

        const future = flat.slice(globalIndex + 1);
        const counts = future.map((ref) =>
          getActiveSellersCount(updated[ref.pIdx].days[ref.dIdx].date, sellers, absences)
        );
        const totalCounts = counts.reduce((s, c) => s + c, 0);

        let distributed = 0;
        future.forEach((ref, idx) => {
          let amount = 0;
          if (totalCounts > 0) {
            amount = round2((remaining * counts[idx]) / totalCounts);
          }
          distributed = round2(distributed + amount);

          if (idx === future.length - 1) {
            const diff = round2(remaining - distributed);
            amount = round2(amount + diff);
          }

          updated[ref.pIdx].days[ref.dIdx].amount = amount;
        });

        // recalcula pesos com base nos valores (amount)
        updated.forEach((period) => {
          const totalAmount = period.days.reduce((s, d) => s + (d.amount || 0), 0);

          let weightSum = 0;
          period.days.forEach((d, idx) => {
            let w = totalAmount > 0 ? round2((d.amount / totalAmount) * 100) : 0;
            weightSum = round2(weightSum + w);

            if (idx === period.days.length - 1) {
              const diff = round2(100 - weightSum);
              w = round2(w + diff);
            }
            d.weight = w;
          });
        });


        setGoal({
          ...goal,
          periods: updated,
        });

        return updated;
      });
    }
  };

  if (periods.length === 0) {
    return <Typography>Carregando períodos...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Quais são os valores por dia da meta?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Para cada período escolhido, informe o peso e valor do respectivo dia.
      </Typography>

      {periods.map((p, periodIndex) => (
        <Accordion key={periodIndex} defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mr: 1, width: "100%" }}
            >
              <Typography variant="subtitle1">
                {p.period}º Período
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dayjs(p.start_date).format("DD/MM/YY")} até{" "}
                {dayjs(p.end_date).format("DD/MM/YY")}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {p.days.length} dias
              </Typography>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Dia</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Vendedores</TableCell> {/* NOVA COLUNA */}
                  <TableCell>Peso (%)</TableCell>
                  <TableCell>Meta (R$)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.days.map((d, dayIndex) => (
                  <TableRow
                    key={d.date}
                    sx={{
                      backgroundColor: dayIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                    }}
                  >
                    <TableCell>{dayIndex + 1}º dia</TableCell>
                    <TableCell>
                      {dayjs(d.date).format("ddd, DD/MM")}
                    </TableCell>
                    <TableCell align="center">{d.activeSellers}</TableCell>
                    <TableCell>
                      <NumericFormat
                        value={d.weight}
                        customInput={TextField}
                        size="small"
                        fullWidth
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          readOnly: true, // <- peso calculado automaticamente
                        }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        value={d.amount}
                        customInput={TextField}
                        size="small"
                        fullWidth
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        onValueChange={(values) =>
                          handleChange(
                            periodIndex,
                            dayIndex,
                            "amount",
                            values.floatValue || 0
                          )
                        }
                        InputProps={{
                          startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                        }}
                        sx={{ width: 150 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell colSpan={2}>
                    <Typography fontWeight="bold">Total do Período</Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <NumericFormat
                      value={p.days
                        .reduce((sum, d) => sum + (parseFloat(d.weight) || 0), 0)
                        .toFixed(2)}
                      displayType="text"
                      thousandSeparator="."
                      decimalSeparator=","
                      suffix=" %"
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </TableCell>
                  <TableCell>
                    <NumericFormat
                      value={getPeriodTotal(p).toFixed(2)}
                      displayType="text"
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="R$ "
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}

      <Box
        mt={2}
        p={2}
        sx={{
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Stack spacing={1} sx={{ width: 250 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight="bold">Meta Original:</Typography>
            <NumericFormat
              value={goal.target}
              displayType="text"
              thousandSeparator="."
              decimalSeparator=","
              prefix="R$ "
              decimalScale={2}
              fixedDecimalScale
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight="bold">Distribuído:</Typography>
            <NumericFormat
              value={totalGeral}
              displayType="text"
              thousandSeparator="."
              decimalSeparator=","
              prefix="R$ "
              decimalScale={2}
              fixedDecimalScale
            />
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight="bold">Diferença:</Typography>
            <NumericFormat
              value={
                goal.target - totalGeral < 0
                  ? (goal.target - totalGeral) * -1
                  : goal.target - totalGeral
              }
              displayType="text"
              thousandSeparator="."
              decimalSeparator=","
              prefix="R$ "
              decimalScale={2}
              fixedDecimalScale
              style={{
                color:
                  totalGeral < goal.target
                    ? "#d32f2f"
                    : totalGeral > goal.target
                    ? "#388e3c"
                    : "#000000",
              }}
            />
          </Stack>
        </Stack>
      </Box>

      <StepNavigation 
        onBack={onBack} 
        activeStep={activeStep}
        onSave={onSave}
      />
    </Box>
  );
}
